<template>
  <v-autocomplete
    outlined
    v-model="configs"
    :items="configList"
    :menu-props="{ maxHeight: '400', zIndex: zindex }"
    :label="label"
    :multiple="true"
    small-chips
    deletable-chips
    item-text="name"
    item-value="id"
    hide-details
    :dense="dense"
  />
</template>

<script>
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";

import {
  computed,
  onMounted,
  reactive,
  set,
  watch,
} from "@vue/composition-api";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";
import { URL_PARAM_NAMES } from "@/utils/constants";
import compact from "lodash/compact";

export default {
  name: "ConfigFilter",
  components: {},
  props: {
    label: {
      type: String,
      default: "Nom de configuration",
    },
    initialValue: {
      type: Number | Array,
      validator(values) {
        if (isArray(values)) {
          return (
            values.length === 0 ||
            values.every((value) => typeof value === "number")
          );
        }

        return !isNaN(values);
      },
    },
    dense: {
      type: Boolean,
      default: false,
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
        - getters["<store>/<storeGetter>"]
        - dispatch("<store>/<storeUpdater>", payload)
        */
        return ["newsRadar"].includes(value);
      },
    },
    storeGetter: {
      type: String,
      default: "getConfigs",
    },
    storeUpdater: {
      type: String,
      default: "updateConfigs",
    },
    zindex: {
      type: Number,
      default: 13,
    },
    site: {
      type: Number,
      default: 0,
    },
    noUrl: {
      type: Boolean,
      default: false,
    },
    defaultAllChecked: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: [], // in case we need to load from url params
    });

    // =>  METHODS

    // =>  COMPUTED
    const viaStore = computed(() => props.store !== undefined);
    const initialValue = computed(() =>
      props.noUrl
        ? props.initialValue
        : page.initialUrlValue ?? props.initialValue
    );

    const configList = computed(() => store.getters["newsRadar/getConfigList"]);

    const configs = computed({
      get: () => {
        if (viaStore.value) {
          return store.getters[`${props.store}/${props.storeGetter}`];
        }

        return initialValue.value;
      },
      set: (value) => {
        if (viaStore.value) {
          store.dispatch(`${props.store}/${props.storeUpdater}`, value);

          if (page.isInitialized && !props.noUrl) {
            // Only update user changes, not initialization
            addQueryStringParam({
              param: "configs",
              value: value,
              is_multiple: true,
              is_integer: true,
              // is_boolean: false,
            });
          }
        }

        if (!isEqual(value, initialValue?.value)) {
          emit("valueChanged", value);
        }
      },
    });

    // =>  WATCH
    watch(
      () => props.site,
      async (newValue) => {
        await store.dispatch("newsRadar/getConfigList", newValue);
      }
    );

    // Watch store site changes , if site changes, get the config list for the new site
    watch(
      () => store.getters["newsRadar/getSites"],
      async (newSite) => {
        const siteValue = (isArray(newSite) && newSite.length > 0) ? newSite[0] : newSite;
        if (siteValue && siteValue !== 0 && siteValue !== props.site) {
          await store.dispatch("newsRadar/getConfigList", siteValue);
        }
      }
    );

    watch(configList, () => {
      if (props.defaultAllChecked) {
        if (props.site) {
          configs.value = configList.value.map((conf) => conf.id);
        }
      } else {
        if(initialValue.value) {
          configs.value = compact(
            configList.value.map((conf) =>
                initialValue.value.includes(conf.id) ? conf.id : undefined
              )
            );
        }
      }
    });

    // =>  HOOKS
    onMounted(async () => {
      if (props.site) {
        await store.dispatch("newsRadar/getConfigList", props.site);
      }

      if (viaStore.value && !props.noUrl) {
        set(
          page,
          "initialUrlValue",
          await synchronizeFilterWithQueryString({
            dispatcher: viaStore.value
              ? `${props.store}/${props.storeUpdater}`
              : "",
            param: "configs",
            value: configs.value,
            is_multiple: true,
            is_integer: true,
            // is_boolean: false,
            dependsOn: URL_PARAM_NAMES["SiteFilter"],
          })
        );
      }

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      configList,
      configs,
      page,
    };
  },
};
</script>

<style></style>
